import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { useTranslation } from 'react-i18next';
const QUICK_REPLIES = [
  "How do I set up an agent?",
  "What channels do you support?",
  "How much does it cost?",
  "Can I see a demo?",
];

const KEYWORD_RESPONSES = [
  {
    keywords: ["set up", "setup", "start", "onboard", "how do i"],
    response:
      "Setting up your Max AI Agent takes just 5 minutes:\n\n1. Sign up at maxemployee.com\n2. Connect your channels (WhatsApp, Email, Telegram, etc.)\n3. Upload your knowledge base or let Max learn from your docs\n4. Go live — your agent starts working immediately\n\nNo coding required. Our Setup Assist team can even do it for you!",
  },
  {
    keywords: ["channel", "whatsapp", "telegram", "email", "discord", "slack", "instagram", "messenger", "twitter", "integrat"],
    response:
      "Max AI Agent supports all major channels:\n\n• WhatsApp Business\n• Telegram\n• Email (IMAP/SMTP)\n• Discord\n• Slack\n• Instagram DM\n• Facebook Messenger\n• X/Twitter DM\n• Web Chat\n\nAll managed from a single unified dashboard. Your agent responds consistently across every channel.",
  },
  {
    keywords: ["cost", "price", "pricing", "plan", "how much", "pay", "subscript", "free trial"],
    response:
      "We have plans for every stage:\n\n• Starter: $49/mo — 1 agent, 3 channels\n• Growth: $149/mo — 5 agents, unlimited channels\n• Enterprise: Custom — dedicated VM, SLA, priority support\n\nAll plans include a 7-day free trial. No credit card required to start. Cancel anytime.",
  },
  {
    keywords: ["demo", "see", "show", "try", "test", "example", "watch"],
    response:
      "You're already in a demo! This chat is a live simulation of how Max AI Agent interacts with visitors.\n\nIn production, Max handles real customer conversations 24/7 — answering questions, qualifying leads, booking meetings, and escalating to your team when needed.\n\nWant the full experience? Start your free trial and deploy your first agent in minutes.",
  },
];

const DEFAULT_RESPONSE =
  "Great question! Max AI Agent can handle a wide range of tasks — from customer support to lead qualification to appointment booking.\n\nHere's what makes Max different:\n• Works 24/7 across all your channels\n• Learns from your knowledge base\n• Takes actions, not just replies\n• Self-improves over time\n\nWant to see it in action? Start your free trial at maxemployee.com";

const HIDDEN_PREFIXES = ["/support", "/dashboard", "/checkout", "/admin", "/course", "/login", "/register", "/signup", "/onboarding"];

function getResponse(input) {
  const lower = input.toLowerCase();
  for (const entry of KEYWORD_RESPONSES) {
    if (entry.keywords.some((kw) => lower.includes(kw))) {
      return entry.response;
    }
  }
  return DEFAULT_RESPONSE;
}

function TypingIndicator() {
  const { t } = useTranslation();

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-2xl rounded-bl-none border-l-2 border-amber-500 bg-zinc-800 px-4 py-3">
        <div className="flex items-center gap-1.5" aria-label={t('interactive_demo.max_is_typing')}>
          <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  );
}

export default function InteractiveDemo() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [quickRepliesVisible, setQuickRepliesVisible] = useState(true);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const hidden = HIDDEN_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const sendMessage = (text) => {
    const trimmed = text.trim();
    if (!trimmed || typing) return;

    const userMsg = { id: Date.now(), sender: "user", text: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setTyping(true);
    setQuickRepliesVisible(false);

    setTimeout(() => {
      const response = getResponse(trimmed);
      const botMsg = { id: Date.now() + 1, sender: "bot", text: response };
      setMessages((prev) => [...prev, botMsg]);
      setTyping(false);
    }, 1200 + Math.random() * 800);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleQuickReply = (text) => {
    sendMessage(text);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setOpen(false);
    }
  };

  if (hidden) return null;

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t('interactive_demo.try_max_ai_agent_demo')}
          className="fixed bottom-24 right-6 z-[40] inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-zinc-950/95 px-4 py-3 text-sm text-white shadow-2xl shadow-black/40 transition-all hover:scale-105 hover:border-amber-400 hover:shadow-amber-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
        >
          <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-black">
            <MessageCircle className="h-4 w-4" />
            <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-zinc-950 bg-green-500" />
          </span>
          <span className="font-mono text-xs uppercase tracking-wider text-amber-300">{t('interactive_demo.try_max_ai_agent')}</span>
          <span className="absolute inset-0 rounded-full animate-ping bg-amber-500/20 pointer-events-none" />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-[40] w-[min(400px,calc(100vw-2rem))] h-[500px] flex flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-[#09090B] shadow-2xl shadow-black/60 animate-in"
          role="dialog"
          aria-modal="true"
          aria-label={t('interactive_demo.max_ai_agent_live_demo')}
          onClick={handleBackdropClick}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-zinc-800 bg-zinc-950/95 px-4 py-3 shrink-0">
            <div className="flex items-center gap-3">
              <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-black">
                <MessageCircle className="h-4 w-4" />
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-zinc-950 bg-green-500" />
              </span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
                  // MAX AI AGENT
                </div>
                <div className="text-xs text-zinc-300">Live Demo</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-full border border-zinc-800 p-2 text-zinc-400 transition-colors hover:border-zinc-600 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              aria-label={t('interactive_demo.close_chat')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" role="log" aria-live="polite">
            {/* Welcome message */}
            {messages.length === 0 && !typing && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-bl-none border-l-2 border-amber-500 bg-zinc-800 px-4 py-3 text-sm leading-relaxed text-zinc-100">
                  <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300">
                    Max AI
                  </div>
                  Hi! I'm Max, your AI digital worker. Ask me anything about automating your business!
                </div>
              </div>
            )}

            {/* Rendered messages */}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.sender === "user"
                      ? "rounded-br-none border-r-2 border-amber-500 bg-amber-500/10 text-zinc-100"
                      : "rounded-bl-none border-l-2 border-amber-500 bg-zinc-800 text-zinc-100"
                  }`}
                >
                  {msg.sender === "bot" && (
                    <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300">
                      Max AI
                    </div>
                  )}
                  {msg.text}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {typing && <TypingIndicator />}

            {/* Quick Replies */}
            {quickRepliesVisible && messages.length === 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {QUICK_REPLIES.map((qr) => (
                  <button
                    key={qr}
                    type="button"
                    onClick={() => handleQuickReply(qr)}
                    className="rounded-full border border-amber-500/30 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:bg-amber-500/10 hover:border-amber-500/50 hover:text-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                  >
                    {qr}
                  </button>
                ))}
              </div>
            )}

            {/* CTA after some conversation */}
            {messages.length >= 4 && !typing && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-bl-none border-l-2 border-amber-500 bg-zinc-800 px-4 py-3 text-sm leading-relaxed">
                  <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300">
                    Max AI
                  </div>
                  <div className="text-zinc-100">
                    Ready to deploy your own?{" "}
                    <Link
                      to="/pricing"
                      className="inline-flex items-center gap-1 font-semibold text-amber-400 underline underline-offset-2 hover:text-amber-300 transition-colors"
                      onClick={() => setOpen(false)}
                    >
                      Start Free Trial →
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="border-t border-zinc-800 p-3 shrink-0">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('interactive_demo.ask_max_anything')}
                aria-label={t('interactive_demo.chat_message_input')}
                className="flex-1 rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-amber-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500"
              />
              <button
                type="submit"
                disabled={typing || !input.trim()}
                aria-label={t('interactive_demo.send_message')}
                className="inline-flex items-center justify-center rounded-xl bg-amber-500 px-3 py-2.5 text-black transition-colors hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
